import { useMemo } from "react";
import { useSelector } from "react-redux";
import { BadgeCheck } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "components/ui/avatar";
import { Badge } from "components/ui/badge";
import {
  getAuthUserAvatarUrl,
  getAuthUserDisplayName,
  getAuthUserInitials,
  isAuthUserEmailVerified,
} from "lib/authUserDisplay";
import { selectAuthUser } from "store/reducer";
import { cn } from "lib/utils";

export default function SettingsUserSummary({ className }) {
  const user = useSelector(selectAuthUser);
  const displayName = useMemo(
    () => getAuthUserDisplayName(user) || "User",
    [user]
  );
  const avatarUrl = useMemo(() => getAuthUserAvatarUrl(user), [user]);
  const initials = useMemo(() => getAuthUserInitials(user), [user]);
  const emailVerified = useMemo(() => isAuthUserEmailVerified(user), [user]);
  const investorTypeLabel = useMemo(() => {
    const type = String(user?.investorType ?? "").trim().toLowerCase();
    if (!type) return "";
    return type.charAt(0).toUpperCase() + type.slice(1);
  }, [user]);

  return (
    <div
      className={cn(
        "flex max-w-xl items-center gap-5 rounded-lg border border-border bg-card p-6",
        className
      )}
    >
      <Avatar className="h-20 w-20 shrink-0 border border-border">
        <AvatarImage src={avatarUrl || undefined} alt="" />
        <AvatarFallback className="bg-primary text-lg font-semibold text-primary-foreground">
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-lg font-semibold text-foreground">{displayName}</p>
          {investorTypeLabel ? (
            <Badge variant="secondary" className="shrink-0 capitalize">
              {investorTypeLabel}
            </Badge>
          ) : null}
        </div>
        <p className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
          <span className="truncate">{user?.email || "—"}</span>
          {emailVerified ? (
            <BadgeCheck
              className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-500"
              aria-label="Email verified"
              title="Email verified"
            />
          ) : null}
        </p>
      </div>
    </div>
  );
}
